import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/shared/ui';

export type Props = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  invalid?: boolean;
  allowRevealedPassword?: boolean;
  revealed?: boolean;
  onToggleRevealed?: (revealed: boolean) => void;
};

/**
 * Password input, including a button to reveal the password as plain text.
 *
 * Pass in `revealed` and `onToggleRevealed` to control the state externally,
 * otherwise it will be managed automatically by this component.
 */
export const PasswordInput = (props: Props) => {
  const {
    invalid,
    allowRevealedPassword = true,
    revealed: revealedProps,
    onToggleRevealed,
    ...inputProps
  } = props;
  const [revealedState, setRevealedState] = React.useState(false);
  const node = React.useRef<HTMLInputElement | null>(null);

  const isRevealed = revealedProps !== undefined ? revealedProps : revealedState;

  const toggleRevealed = (checked: boolean) => {
    if (onToggleRevealed) {
      onToggleRevealed(checked);
    } else {
      setRevealedState(checked);
    }
  };

  return (
    <div className="relative">
      <Input
        ref={node}
        type={isRevealed ? 'text' : 'password'}
        variant={invalid ? 'destructive' : undefined}
        {...inputProps}
        className={cn('pr-11', inputProps.className)}
      />

      {allowRevealedPassword && (
        <div className="absolute right-0 top-0 flex items-center h-full pr-2">
          <Button
            size="icon"
            variant="ghost"
            className="[&_svg]:size-5 w-8 h-8 rounded-full opacity-60 hover:opacity-100"
            onClick={() => toggleRevealed(!isRevealed)}
            aria-label="Show password"
          >
            {isRevealed ? <EyeOff /> : <Eye />}
          </Button>
        </div>
      )}
    </div>
  );
};
